import React from "react";
import { Link, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import { BlockRenderer } from "@/voices/BlockRenderer";
import type { VoicePost } from "@/voices/types";

interface PostResponse {
  post: VoicePost;
}

function formatDate(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export function VoiceDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = React.useState<
    | { status: "loading" }
    | { status: "found"; post: VoicePost }
    | { status: "not_found" }
    | { status: "error"; message: string }
  >({ status: "loading" });

  React.useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/voices/posts/${encodeURIComponent(slug)}`);
        if (res.status === 404) {
          if (!cancelled) setState({ status: "not_found" });
          return;
        }
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as PostResponse;
        if (!cancelled) setState({ status: "found", post: data.post });
      } catch (err) {
        if (!cancelled) setState({ status: "error", message: String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (state.status === "loading") {
    return (
      <PageShell title="Loading…" status="loading">
        <Seo title="Loading…" description="" path={`/voices/${slug ?? ""}`} noindex />
        <p className="font-mono text-xs text-ink-3 uppercase tracking-widest">
          Loading…
        </p>
      </PageShell>
    );
  }

  if (state.status === "not_found") {
    return (
      <PageShell title="Not found" status="404">
        <Seo
          title="Not found — Voices"
          description="No such post."
          path={`/voices/${slug ?? ""}`}
          noindex
        />
        <p className="font-serif text-lg text-ink-2 leading-relaxed">
          That post is not published, was archived, or never existed.{" "}
          <Link to="/voices" className="text-claude underline">
            Browse all voices →
          </Link>
        </p>
      </PageShell>
    );
  }

  if (state.status === "error") {
    return (
      <PageShell title="Error" status="error">
        <Seo
          title="Error loading post"
          description=""
          path={`/voices/${slug ?? ""}`}
          noindex
        />
        <div className="border border-red-500/40 bg-red-500/5 px-4 py-3 font-mono text-xs text-red-500">
          Could not load post: {state.message}
        </div>
      </PageShell>
    );
  }

  const { post } = state;

  return (
    <>
      <Seo
        title={post.seo?.title ?? `${post.title} — Voices`}
        description={post.seo?.description ?? post.excerpt}
        path={`/voices/${post.slug}`}
        ogImage={post.seo?.ogImage ?? post.coverImage}
        article={{
          published: post.publishedAt ?? undefined,
          modified: post.updatedAt,
          author: post.author.name,
          section: post.category ?? "Voices",
        }}
      />
      <article className="flex flex-col w-full max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 animate-in fade-in duration-700">
        <header className="mb-12 pb-8 border-b border-hairline">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-4">
            <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
              {formatDate(post.publishedAt)}
            </p>
            {post.category && (
              <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest">
                {post.category}
              </p>
            )}
          </div>
          <h1 className="font-serif font-normal text-4xl md:text-5xl text-ink leading-tight mb-4 tracking-tight">
            {post.title}
          </h1>
          {post.subtitle && (
            <p className="font-serif text-xl text-ink-2 leading-relaxed mb-6">
              {post.subtitle}
            </p>
          )}
          <p className="font-mono text-xs text-ink-3">
            by{" "}
            <Link
              to={`/voices/by/${post.author.slug}`}
              className="text-ink hover:text-claude underline"
            >
              {post.author.name}
            </Link>
            {post.author.bioSnippet && (
              <span className="text-ink-3"> — {post.author.bioSnippet}</span>
            )}
          </p>
        </header>

        {post.coverImage && (
          <figure className="mb-12 -mx-6 md:-mx-10">
            <img src={post.coverImage} alt="" className="w-full h-auto" loading="eager" />
          </figure>
        )}

        <BlockRenderer blocks={post.blocks} className="prose-voice" />

        <footer className="mt-16 pt-8 border-t border-hairline">
          <p className="font-mono text-[10px] text-ink-3 uppercase tracking-widest mb-3">
            About the author
          </p>
          <h3 className="font-serif text-xl text-ink mb-2">
            <Link
              to={`/voices/by/${post.author.slug}`}
              className="hover:text-claude transition-colors"
            >
              {post.author.name}
            </Link>
          </h3>
          {post.author.bioSnippet && (
            <p className="font-serif text-base text-ink-2 leading-relaxed mb-3">
              {post.author.bioSnippet}
            </p>
          )}
          {post.author.didOrPersonRef && (
            <p className="font-mono text-xs text-ink-3">
              <Link
                to={post.author.didOrPersonRef}
                className="underline hover:text-claude"
              >
                .person profile →
              </Link>
            </p>
          )}
        </footer>

        <p className="mt-12 font-mono text-[10px] text-ink-3 uppercase tracking-widest">
          <Link to="/voices" className="underline hover:text-ink">
            ← all voices
          </Link>
        </p>
      </article>
    </>
  );
}
